import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { projectService } from '../services/projectService';
import { ErrorNote, Field, Modal } from './ui';

interface CreateProjectModalProps {
  onClose: () => void;
  onSuccess: (projectId: string) => void;
}

const CreateProjectModal: React.FC<CreateProjectModalProps> = ({ onClose, onSuccess }) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !name.trim()) return;
    setError('');
    setLoading(true);
    try {
      const id = await projectService.createProject(name, description, user.uid);
      toast.success(t('projects.created'));
      onSuccess(id);
    } catch (err) {
      console.error('Error creating project:', err);
      setError(t('projects.createFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={t('projects.new')}
      onClose={onClose}
      busy={loading}
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-secondary" disabled={loading}>
            {t('common.cancel')}
          </button>
          <button type="submit" form="create-project" className="btn btn-primary" disabled={loading || !name.trim()}>
            {loading ? t('common.working') : t('projects.create')}
          </button>
        </>
      }
    >
      <form id="create-project" onSubmit={handleSubmit} className="space-y-4">
        <Field label={t('projects.name')} htmlFor="project-name">
          <input id="project-name" className="input" value={name} onChange={(e) => setName(e.target.value)} required maxLength={200} />
        </Field>
        <Field label={t('projects.description')} htmlFor="project-description" hint={t('projects.descriptionHint')}>
          <textarea
            id="project-description"
            className="input"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>
        {error && <ErrorNote>{error}</ErrorNote>}
      </form>
    </Modal>
  );
};

export default CreateProjectModal;
